/* The average cost a lot's own trades add up to.
 *
 * `Holding.avgCost` is stored, and has to be: most books arrive without a trade
 * history at all — seeded from a snapshot, imported from a broker that only
 * gives positions — and for those the average is the only cost record there is.
 *
 * But where the trades *are* complete, the stored figure is a summary of them,
 * and a summary that does not move when the thing it summarises is corrected is
 * simply stale. Correcting a purchase from 43.23 to 14.41 and watching the
 * position go on reporting a nineteen percent loss is that staleness, and it
 * reads exactly like the app ignoring the correction.
 *
 * So: derived when it can be, left alone when it cannot.
 *
 * ── the test for "can be" ─────────────────────────────────────────────────
 *
 * The trades have to account for the shares held, to a hair. If they say twenty
 * and the lot says twenty, the trades are the whole story and their weighted
 * average is the cost. If they say three and the lot says twenty, seventeen
 * shares got there some other way and any average from those three would be a
 * confident number about a position it knows nothing about.
 *
 * Sells reduce the share count and leave the average where it is, which is what
 * average-cost accounting does and what `ReduceClose` already assumes when it
 * measures a realised gain against `avgCost`. FIFO would give a different
 * answer and a different tax figure; the book has always used the average, and
 * this follows it rather than introducing a second convention. */

import type { Holding, Trade } from '../types';

export type BasisRefusal = 'no-trades' | 'shares-differ' | 'nothing-held';

export interface BasisCheck {
  /** The average the trades come to, or null when they cannot say. */
  avgCost: number | null;
  /** Shares the trades account for, whatever the lot says. */
  netShares: number;
  /** Why not, when it is null. */
  why?: BasisRefusal;
}

/** A hair, in shares. Fractional holdings are ordinary and floating point does
    not add cleanly; a millionth of a share is not somebody's untracked purchase. */
const CLOSE_ENOUGH = 1e-6;

export function basisFromTrades(trades: Trade[], holding: Holding): BasisCheck {
  const mine = trades
    .filter((t) => t.ticker === holding.ticker && t.account === holding.account)
    .sort((a, b) => a.at.localeCompare(b.at));

  if (mine.length === 0) return { avgCost: null, netShares: 0, why: 'no-trades' };

  let shares = 0;
  let average = 0;

  for (const t of mine) {
    const size = Math.abs(t.shares);
    if (!(size > 0)) continue;

    if (t.side === 'buy') {
      const paid = size * Math.abs(t.price) + Math.abs(t.fee ?? 0);
      const total = shares + size;
      average = total > 0 ? (shares * average + paid) / total : 0;
      shares = total;
    } else {
      /* A sale takes shares off and leaves the average alone. The commission on
         it is a cost of getting out, which belongs in the realised result and
         not in what the remaining shares cost — see `state/commission.ts`. */
      shares = Math.max(0, shares - size);
      if (shares === 0) average = 0;
    }
  }

  if (shares <= CLOSE_ENOUGH) return { avgCost: null, netShares: shares, why: 'nothing-held' };
  if (Math.abs(shares - holding.shares) > CLOSE_ENOUGH) {
    return { avgCost: null, netShares: shares, why: 'shares-differ' };
  }
  return { avgCost: average, netShares: shares };
}

/** The lot as it should stand given its trades, or null when nothing should
    change — either because the trades cannot say, or because they already
    agree with what is stored. */
export function lotFollowingTrades(trades: Trade[], holding: Holding): Holding | null {
  const check = basisFromTrades(trades, holding);
  if (check.avgCost === null) return null;
  if (Math.abs(check.avgCost - holding.avgCost) < 1e-9) return null;
  return { ...holding, avgCost: check.avgCost };
}


/* Every lot whose stored average has fallen behind the trades that fully
 * account for it.
 *
 * Run when the book loads, beside the mark pruning, for the reason that made
 * the first attempt useless: recomputing only when a trade is saved fixes
 * nothing for trades corrected before the recompute existed. A book carries its
 * history, and the correction has to reach the history rather than only the
 * next edit.
 *
 * It writes only where there is a disagreement, so it is a no-op on every load
 * after the first — and it can only fire where the trades account for the whole
 * lot, which is the condition that makes the stored figure a summary of them
 * rather than a record in its own right. */
export function lotsOutOfStep(trades: Trade[], holdings: Holding[]): Holding[] {
  const out: Holding[] = [];
  for (const h of holdings) {
    const next = lotFollowingTrades(trades, h);
    if (next) out.push(next);
  }
  return out;
}
